import { AvatarWithDisplayValueColumn } from 'src/components/DataTable';
import { GroupHoverCard } from 'src/components/GroupHoverCard/GroupHoverCard';
import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { LegacyModal } from 'src/components/Modal/LegacyModal';
import { generatePass } from 'src/utils/password/password';
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { requireField } from 'src/constants/constants';
import { notifyAboutException, notifyAboutSuccess } from 'src/utils/notify';
import { Dropdown as ApplicationDropdown } from 'src/components/Dropdown';
import {
    ConfigProvider,
    Empty,
    Select,
    Table,
    TablePaginationConfig,
} from 'antd';
import { InputText } from 'primereact/inputtext';
import { FormItem } from 'src/components/Form';
import { useSearchParams } from 'react-router-dom';
import { Group, ResponseError, useGetGroupsQuery } from 'src/api';
import { Helmet } from 'react-helmet-async';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import { find, forEach, map, size } from 'lodash';
import './module.scss';
import {
    AddStudentAccountArg,
    Student__Legacy,
    StudentStatus,
    UpdateStudentAccountArg,
    useAddStudentAccountMutation,
    useGetStudentsQuery,
} from 'src/api/endpoints/students';
import { getUserFullname } from 'src/utils/getFullname';
import { PrimaryButton } from 'src/components/design-system/form/buttons/PrimaryButton';
import { DebouncedSearch } from 'src/components/design-system/inputs/DebouncedSearch';
import { ArrowDownIcon } from 'src/icons/ArrowDown';
import classNames from 'classnames';
import { skipToken } from '@reduxjs/toolkit/query';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';
import { MainSection } from 'src/components/design-system/struct/MainSection';
import { ColorRing } from 'src/components/shared/loader';

export const LOGIN_REGEX = /^[A-Za-z0-9_\-.-@+]{6,}$/;

export enum RegisterStatus {
    All = 'all',
    RegisteredOnly = 'registered-only',
    UnregisteredOnly = 'unregistered-only',
}

type StudentsInitialState = Partial<UpdateStudentAccountArg> &
    Partial<AddStudentAccountArg>;

type StudentsForm = UpdateStudentAccountArg & AddStudentAccountArg;

export type StudentsPageProps = {
    features: {
        filter: {
            registerStatus: boolean;
            group: boolean;
            academicStatus: boolean;
        };
        search: boolean;
    };
};

export const StudentsPage: FC<StudentsPageProps & AuthorizedUserProps> = (
    props: StudentsPageProps & AuthorizedUserProps
): ReactElement => {
    const { features, currentBranchId } = props;

    const [modal, setModal] = useState<boolean>(false);

    const [add, { isLoading: isAddLoading }] = useAddStudentAccountMutation();

    const onCreate = async (form: AddStudentAccountArg): Promise<void> => {
        return add({
            params: { branchId: props.currentBranchId },
            body: {
                login: form.login,
                password: form.password,
                groupId: form.curGroup,
                contract: form.contract,
            },
        })
            .unwrap()
            .then(() => {
                notifyAboutSuccess({ content: 'Студент успешно создан' });
            })
            .catch((m: ResponseError) => {
                notifyAboutException({ content: m.message });
                throw m;
            });
    };

    const [config, setConfig] = useState({
        page: 1,
        size: 50,
    });

    const [query, setQuery] = useState<string>('');

    const [registerStatus, setRegisterStatus] = useState<RegisterStatus>(
        RegisterStatus.RegisteredOnly
    );
    const [selectedGroupId, selectGroupId] = useState<number | string>('all');
    const [selectedAcademicStatuses, selectAcademicStatuses] = useState<
        StudentStatus[]
    >(features.filter.academicStatus ? [0] : []);

    const [searchParams, setSearchParams] = useSearchParams();

    const { data: groups, isLoading: isGroupsLoading } = useGetGroupsQuery({
        params: { branchId: props.currentBranchId },
    });

    const [isRegisterStatusInitialized, setRegisterStatusInitialized] =
        useState<boolean>(false);

    useEffect(() => {
        setSearchParams(() => {
            const returnEmpty = () => {
                searchParams.delete('register');
                setRegisterStatusInitialized(true);
                return searchParams;
            };

            if (!features.filter.registerStatus) return returnEmpty();

            const validate = (v?: string | null): boolean => {
                return !(
                    !v ||
                    !Object.values(RegisterStatus).includes(v as RegisterStatus)
                );
            };

            const value: string | null = searchParams.get('register');

            if (!validate(value)) return returnEmpty();

            setRegisterStatus(value as RegisterStatus);
            setRegisterStatusInitialized(true);

            return searchParams;
        });
    }, [features.filter.registerStatus, searchParams]);

    const [isGroupIdInitialized, setGroupIdInitialized] =
        useState<boolean>(false);

    useEffect(() => {
        if (isGroupsLoading) return;

        setSearchParams(() => {
            const returnEmpty = () => {
                searchParams.delete('group');
                setGroupIdInitialized(true);
                return searchParams;
            };

            if (!features.filter.group) return returnEmpty();
            if (!size(groups)) return returnEmpty();

            const validate = (v?: string | null): boolean => {
                return !(!v || !find(groups, g => g.id === Number(v)));
            };

            const value: string | null = searchParams.get('group');

            if (!validate(value)) return returnEmpty();

            selectGroupId(Number(value));
            setGroupIdInitialized(true);

            return searchParams;
        });
    }, [features.filter.group, searchParams, groups, isGroupsLoading]);

    const [isAcademicStatusesInitialized, setAcademicStatusesInitialized] =
        useState<boolean>(false);

    useEffect(() => {
        setSearchParams(() => {
            const returnEmpty = () => {
                searchParams.delete('status');
                setAcademicStatusesInitialized(true);
                return searchParams;
            };

            if (!features.filter.academicStatus) return returnEmpty();

            const value: string[] = searchParams.getAll('status');

            selectAcademicStatuses(map(value, Number));
            setAcademicStatusesInitialized(true);

            return searchParams;
        });
    }, [features.filter.academicStatus, searchParams]);

    const [isQueryInitialized, setQueryInitialized] = useState<boolean>(false);

    useEffect(() => {
        setSearchParams(() => {
            const returnEmpty = () => {
                searchParams.delete('query');
                setQueryInitialized(true);
                return searchParams;
            };

            if (!features.search) return returnEmpty();

            const value: string | null = searchParams.get('query');

            setQuery(value ?? '');
            setQueryInitialized(true);

            return searchParams;
        });
    }, [features.search, searchParams]);

    const isParamsInitialized: boolean = useMemo(() => {
        return (
            isQueryInitialized &&
            isGroupIdInitialized &&
            isAcademicStatusesInitialized &&
            isRegisterStatusInitialized
        );
    }, [
        isQueryInitialized,
        isGroupIdInitialized,
        isAcademicStatusesInitialized,
        isRegisterStatusInitialized,
    ]);

    useEffect(() => {
        if (isParamsInitialized) return;

        setSearchParams(() => {
            if (features.filter.registerStatus && !searchParams.has('register'))
                searchParams.set('register', registerStatus);

            if (features.filter.group && !searchParams.has('group'))
                searchParams.set('group', selectedGroupId.toString());

            if (features.filter.academicStatus && !searchParams.has('status'))
                forEach(selectedAcademicStatuses, (academicStatus: number) => {
                    searchParams.delete('status');
                    searchParams.append('status', academicStatus.toString());
                });

            if (features.search && !searchParams.has('query'))
                searchParams.set('query', query);

            return searchParams;
        });
    }, [
        isParamsInitialized,
        features,
        searchParams,
        registerStatus,
        selectedGroupId,
        selectedAcademicStatuses,
        query,
    ]);

    const { data, isFetching } = useGetStudentsQuery(
        !isParamsInitialized
            ? skipToken
            : {
                  params: {
                      branchId: props.currentBranchId,
                  },
                  search: {
                      size: config.size,
                      page: config.page - 1,
                      query,
                      groupId:
                          selectedGroupId === 'all'
                              ? undefined
                              : Number(selectedGroupId),
                      registered:
                          registerStatus === RegisterStatus.All
                              ? undefined
                              : registerStatus ===
                                RegisterStatus.RegisteredOnly,
                      statuses: selectedAcademicStatuses
                          .slice()
                          .sort((a, b) => a - b),
                  },
              }
    );

    return (
        <>
            <Helmet title={'Студенты'} />
            <LegacyModal
                header={'Добавление студента'}
                visible={modal}
                onHide={(): void => {
                    setModal(false);
                }}
            >
                <StudentModalContent__Legacy
                    initialState={null}
                    branchId={props.currentBranchId}
                    onSubmit={onCreate}
                    update={false}
                    loading={isAddLoading}
                />
            </LegacyModal>

            <StudentLayout auth={props}>
                <MainSection
                    title={'Студенты'}
                    actions={
                        <>
                            <PrimaryButton
                                label={'Добавить студента'}
                                onClick={() => {
                                    setModal(true);
                                }}
                            />
                        </>
                    }
                >
                    <div className="controls">
                        {features.search && (
                            <DebouncedSearch
                                value={query}
                                onSearch={q => {
                                    if (!isParamsInitialized) return;

                                    setQuery(q);

                                    setSearchParams(() => {
                                        searchParams.set('query', q);
                                        return searchParams;
                                    });

                                    setConfig(prev => ({
                                        ...prev,
                                        page: 1,
                                    }));
                                }}
                                isLoading={isFetching}
                                placeholder={
                                    'Искать по имени, почте, номеру договора студента'
                                }
                                debounce={500}
                            />
                        )}

                        <div className="filters">
                            <Select
                                suffixIcon={
                                    <ArrowDownIcon
                                        className={'select-input-icon'}
                                        color={'#092433'}
                                    />
                                }
                                classNames={{
                                    root: 'select-input',
                                }}
                                options={[
                                    {
                                        label: 'Все статусы регистрации',
                                        value: RegisterStatus.All,
                                    },
                                    {
                                        label: 'Прошли регистрацию',
                                        value: RegisterStatus.RegisteredOnly,
                                    },
                                    {
                                        label: 'Не прошли регистрацию',
                                        value: RegisterStatus.UnregisteredOnly,
                                    },
                                ]}
                                onSelect={v => {
                                    setRegisterStatus(v);

                                    setSearchParams(() => {
                                        searchParams.set('register', v);
                                        return searchParams;
                                    });

                                    setConfig(prev => ({
                                        ...prev,
                                        page: 1,
                                    }));
                                }}
                                value={registerStatus ?? RegisterStatus.All}
                                placeholder={'Выберите статус регистрации'}
                            />

                            <Select
                                disabled={!isGroupsLoading && !size(groups)}
                                loading={isGroupsLoading}
                                popupMatchSelectWidth={true}
                                showSearch={true}
                                optionFilterProp={'label'}
                                suffixIcon={
                                    <ArrowDownIcon
                                        className={'select-input-icon'}
                                        color={'#092433'}
                                    />
                                }
                                classNames={{
                                    root: 'select-input',
                                }}
                                options={[
                                    {
                                        label: 'Все группы',
                                        value: 'all',
                                    },
                                    ...map(groups, (g: Group) => ({
                                        label: g.name,
                                        value: g.id,
                                    })),
                                ]}
                                onSelect={id => {
                                    selectGroupId(id);

                                    setSearchParams(() => {
                                        searchParams.set(
                                            'group',
                                            id.toString()
                                        );
                                        return searchParams;
                                    });

                                    setConfig(prev => ({
                                        ...prev,
                                        page: 1,
                                    }));
                                }}
                                value={selectedGroupId}
                                placeholder={'Выберите группу'}
                            />

                            <Select
                                showSearch={false}
                                maxTagCount={1}
                                maxTagPlaceholder={values => {
                                    return `+${values.length}`;
                                }}
                                tagRender={value => {
                                    return (
                                        <div
                                            className={classNames(
                                                'multiple-select-item',
                                                {
                                                    max: value.isMaxTag,
                                                }
                                            )}
                                        >
                                            {value.label}
                                        </div>
                                    );
                                }}
                                suffixIcon={
                                    <ArrowDownIcon
                                        className={'select-input-icon'}
                                        color={'#092433'}
                                    />
                                }
                                mode={'multiple'}
                                classNames={{
                                    root: 'select-input multiple placeholder-as-value',
                                }}
                                options={[
                                    {
                                        label: 'Действующие',
                                        value: 0,
                                    },
                                    {
                                        label: 'Выпущенные',
                                        value: 1,
                                    },
                                    {
                                        label: 'Отчисленные',
                                        value: 2,
                                    },
                                    {
                                        label: 'В академическом отпуске',
                                        value: 3,
                                    },
                                ]}
                                onChange={v => {
                                    selectAcademicStatuses(v);

                                    setSearchParams(() => {
                                        searchParams.delete('status');

                                        forEach(v, s => {
                                            searchParams.append(
                                                'status',
                                                s.toString()
                                            );
                                        });

                                        return searchParams;
                                    });

                                    setConfig(prev => ({
                                        ...prev,
                                        page: 1,
                                    }));
                                }}
                                value={selectedAcademicStatuses}
                                placeholder={'Все учебные статусы'}
                            />
                        </div>
                    </div>

                    <div
                        className={'student-table'}
                        style={{ marginTop: '24px', width: '100%' }}
                    >
                        <div className={'students-quantity'}>
                            {'Студентов: '}
                            <b
                                style={{
                                    fontWeight: 'bold',
                                }}
                            >
                                {data?.page?.totalElements}
                            </b>
                        </div>
                        <ConfigProvider
                            renderEmpty={() => {
                                return (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={'Студенты не найдены'}
                                    />
                                );
                            }}
                        >
                            <Table<Student__Legacy>
                                loading={{
                                    spinning:
                                        !isParamsInitialized || isFetching,
                                    indicator: (
                                        <ColorRing
                                            width={50}
                                            strokeWidth={5}
                                            color={'#eb4247'}
                                            speedScale={1}
                                        />
                                    ),
                                }}
                                tableLayout={'fixed'}
                                dataSource={data?.content}
                                onChange={(
                                    pagination: TablePaginationConfig
                                ) => {
                                    setConfig({
                                        size: 50,
                                        page: pagination.current ?? 0,
                                    });

                                    if (pagination.current !== config.page)
                                        window.scrollTo({ top: 0 });
                                }}
                                rowKey={s => s.id}
                                pagination={{
                                    hideOnSinglePage: true,
                                    responsive: true,
                                    showSizeChanger: false,
                                    showQuickJumper: false,
                                    total: data?.page?.totalElements,
                                    pageSize: config.size,
                                    current: config.page,
                                    position: ['bottomRight'],
                                }}
                            >
                                <Table.Column<Student__Legacy>
                                    width={'30%'}
                                    title={'Студент'}
                                    render={(_, t: Student__Legacy) => (
                                        <AvatarWithDisplayValueColumn
                                            data={{
                                                userId: t.id,
                                                displayValue:
                                                    getUserFullname(t),
                                                description: t.login,
                                            }}
                                            link={`/b/${currentBranchId}/students/${t.studentId}`}
                                        />
                                    )}
                                />

                                <Table.Column
                                    width={'20%'}
                                    title={'Номер договора'}
                                    dataIndex={'contractNumber'}
                                />

                                <Table.Column
                                    title={'Группа'}
                                    render={(_, t) => (
                                        <GroupHoverCard
                                            branch={currentBranchId}
                                            group={t.group}
                                        />
                                    )}
                                />

                                <Table.Column
                                    title={'Учебный статус'}
                                    dataIndex={'status'}
                                    render={s => s.name}
                                />

                                <Table.Column
                                    hidden={false}
                                    title={'Телефон'}
                                    dataIndex={'phone'}
                                />

                                <Table.Column
                                    hidden={true}
                                    title={'Почта'}
                                    dataIndex={'email'}
                                />
                            </Table>
                        </ConfigProvider>
                    </div>
                </MainSection>
            </StudentLayout>
        </>
    );
};

interface ModalContentProps {
    onSubmit: (value: Required<StudentsForm>) => Promise<void>;
    initialState: StudentsInitialState | null;
    branchId: number;
    update: boolean;
    loading: boolean;
    freezeGroup?: boolean;
}

export const StudentModalContent__Legacy: FC<ModalContentProps> = ({
    onSubmit,
    initialState,
    update,
    branchId,
    loading,
    freezeGroup = false,
}) => {
    const formik = useFormik<Partial<StudentsForm>>({
        initialValues: {
            login: '',
            password: '',
            curGroup: undefined,
            contract: '',
            studentId: null,
            ...initialState,
        },
        validate: ({ login, password, curGroup, contract }) => {
            const errors: Partial<Record<keyof StudentsForm, any>> = {};

            if (!update) {
                if (!login) {
                    errors.login = requireField;
                } else if (login.trim().length < 6) {
                    errors.login = 'Логин должен быть не менее 6 символов';
                } else if (!login.trim().match(LOGIN_REGEX)) {
                    errors.login =
                        'Логин не может иметь кирриллические символы и пробелы';
                }
            }

            if (!update && !password) errors.password = requireField;
            if (!curGroup) errors.curGroup = requireField;
            if (!contract) errors.contract = requireField;

            return errors;
        },
        onSubmit: values => {
            onSubmit(values as Required<StudentsForm>).then(() => {
                formik.resetForm();
            });
        },
    });

    function generate() {
        formik.setFieldValue('password', generatePass());
    }

    const { login, password, curGroup, contract } = formik.values;

    const { data, isLoading, isFetching } = useGetGroupsQuery({
        params: { branchId },
    });

    return (
        <form onSubmit={formik.handleSubmit} className="p-fluid">
            {update ? null : (
                <FormItem
                    value={login}
                    name={'login'}
                    label={'Логин'}
                    formik={formik}
                >
                    <InputText />
                </FormItem>
            )}
            <FormItem
                value={contract}
                name={'contract'}
                label={'Договор'}
                formik={formik}
            >
                <InputText />
            </FormItem>
            <FormItem
                value={curGroup}
                name={'curGroup'}
                label={'Группа'}
                formik={formik}
            >
                <ApplicationDropdown
                    loading={isLoading || isFetching}
                    options={map(data, ({ id, name }) => ({
                        value: id,
                        label: name,
                    }))}
                    disabled={freezeGroup}
                />
            </FormItem>
            <FormItem
                value={password}
                name={'password'}
                label={'Пароль'}
                formik={formik}
            >
                <InputText />
            </FormItem>
            <Button
                type={'button'}
                className={'mb-24'}
                style={{ width: 'auto', display: 'block' }}
                onClick={generate}
            >
                Сгенерировать пароль
            </Button>
            <Button
                loading={loading}
                type="submit"
                label={update ? 'Обновить' : 'Сохранить'}
                className={'w-auto'}
            />
        </form>
    );
};
